import { useState } from 'react';
import { faUserCircle } from '@fortawesome/pro-regular-svg-icons';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { Button, Icon } from '@nazka/nazka.mapframe.components';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { validatePassword } from '@App/components/RegisterForm/register-form.utils';
import { resetPassword } from '@services/auth.service';
import PasswordTooltip from '@App/components/PasswordTooltip/PasswordTooltip';
import PasswordInput from '@App/components/PasswordInput/PasswordInput';
import { useAuth } from '@App/components/AuthProvider/auth-provider.context';

function ResetPassword({
  setShowLoginModal,
  onClose,
  isLoggingIn,
  loading,
  email,
  code,
  firstSignIn,
}) {
  const { firstSignInMutation } = useAuth();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
  const [error, setError] = useState();
  const [repeatError, setRepeatError] = useState();

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      await resetPassword(email, code, newPassword);
    },
    onSuccess: onClose,
  });

  return (
    <div className="login-modal">
      <div
        className="login-modal__background"
        onClick={() => setShowLoginModal(false)}
      />
      <div className={`login-modal__content${isLoggingIn || loading ? ' is-loading' : ''}`}>
        <div className="login-modal__content__header">
          <div className="login-modal__content__header__title">
            <Icon icon={faUserCircle} className="login-modal__content__header__title__icon" />
            <h2 className="login-modal__content__header__title__text">
              Nieuw wachtwoord instellen
            </h2>
          </div>
          <Button
            icon={faClose}
            layout="ghost"
            hover={false}
            className="login-modal__content__header__close"
            onClick={onClose}
          />
        </div>
        <div className="login-modal__content__form">
          <label
            className={`login-modal__content__form__label${error ? ' login-modal__content__form__label--error' : ''}`}
          >
            <span className="login-modal__content__form__label__text">
              Nieuw wachtwoord
              <PasswordTooltip />
            </span>
            <PasswordInput
              className="login-modal__content__form__label__input"
              value={newPassword}
              onInputChange={v => setNewPassword(v)}
            />
            {error && error.length && (
              <p className="login-modal__content__form__label__error">
                {error}
              </p>
            )}
          </label>
          <label
            className={`login-modal__content__form__label${repeatError ? ' login-modal__content__form__label--error' : ''}`}
          >
            <span className="login-modal__content__form__label__text">
              Bevestig wachtwoord
            </span>
            <PasswordInput
              className="login-modal__content__form__label__input"
              value={newPasswordRepeat}
              onInputChange={v => setNewPasswordRepeat(v)}
            />
            {repeatError && repeatError.length && (
              <p className="login-modal__content__form__label__error">
                {repeatError}
              </p>
            )}
          </label>
        </div>
        {(resetPasswordMutation.isError || firstSignInMutation.isError) && (
          <p className="login-modal__content__form__label__error">
            {resetPasswordMutation.error?.response?.data?.message || firstSignInMutation.error?.response?.data?.message || 'Er ging iets mis.'}
          </p>
        )}
        <Button
          className="login-modal__content__form__reset"
          onClick={async () => {
            const errorArray = [];
            validatePassword(newPassword, errorArray);
            if (errorArray.length) {
              setError(errorArray[0].message);
              setRepeatError(null);
            } else if (newPassword !== newPasswordRepeat) {
              setError(null);
              setRepeatError('Wachtwoorden komen niet overeen.');
            } else {
              setError(null);
              setRepeatError(null);
            }

            if (firstSignIn) {
              await firstSignInMutation.mutateAsync({
                email: firstSignIn.email,
                password: newPassword,
                session: firstSignIn.session,
              });

              navigate('/admin');
            } else {
              resetPasswordMutation.mutate();
            }
            
            window.gtag?.('event', 'forgot-password-modal_click_confirm_new_password', {
              event_category: 'Account',
              event_label: 'Forgot password click confirm new password button',
            });
          }}
          isLoading={resetPasswordMutation.isPending || firstSignInMutation.isPending}
        >
          Nieuw wachtwoord instellen
        </Button>
      </div>
    </div>
  )
}

export default ResetPassword;